/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Typography } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { DecimalDigits } from '@constants/enums';
import { NEW_USER_PERSON } from '../../../../constants/clients';
import {
  Bankaccount,
  FixedBankaccount,
} from '../../../../services/client/types';
import {
  DECIMALS_TO_SHOW,
  camelCaseMapData,
  formatCurrency,
} from '../../../../utils/formater';
import ProtectData from '../../../../utils/protectData.service';
import { useFormData } from '../context';
import { AMOUNT_MONEY } from '../constants/catalogs';
import { isUnsupportedClabe, getUnsupportedBankInfo } from '../utils';

interface DetailViewProps {
  isErrorDetail?: boolean;
  isSuccessDetail?: boolean;
  spacing?: 'nomal' | 'compact';
}

const DetailView = ({
  isErrorDetail = false,
  isSuccessDetail = false,
  spacing = 'nomal',
}: DetailViewProps) => {
  const { t } = useTranslation();
  const { data } = useFormData();

  const {
    amount,
    client: clientSelected,
    clientCurrency,
    currency,
    dueDate,
    payable,
    resultAmount,
    reverse,
    isForward,
    forwardValues,
  } = data;

  const paddingSize = spacing === 'compact' ? 'py-2' : 'py-3';

  const fixedAccount = clientSelected?.bankAccount
    ? camelCaseMapData<Bankaccount, FixedBankaccount>(
        clientSelected.bankAccount
      )
    : undefined;

  const protectedData = new ProtectData();

  const wrapperClasses = `flex justify-between ${paddingSize} flex-col lg:flex-row`;
  const boldeText = '!font-bold !text-xl text-right break-all';

  const hasUnsupportedClabe = isUnsupportedClabe(fixedAccount?.clabe);
  const unsupportedBankInfo = getUnsupportedBankInfo(fixedAccount?.clabe);

  if (!clientSelected) return null;

  return (
    <div
      className={clsx(
        'bg-slate-200',
        isErrorDetail ? 'bg-red-50' : '',
        isSuccessDetail ? 'bg-teal-50' : '',
        'rounded mb-4 w-full p-5'
      )}
    >
      <div className={wrapperClasses}>
        <Typography className="!text-lg">{t('clientName')}: </Typography>
        <Typography className={boldeText}>
          {(clientSelected.entityBusiness === NEW_USER_PERSON
            ? clientSelected.name
            : clientSelected.companyName) || clientSelected.email}
        </Typography>
      </div>
      {fixedAccount && fixedAccount.routingNumber ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('ccRoutingNumber')}: </Typography>
          <Typography className={boldeText}>
            {protectedData.protectString(fixedAccount.routingNumber)}
          </Typography>
        </div>
      ) : null}
      {fixedAccount && fixedAccount.accountNumber ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('ccAccountNumber')}: </Typography>
          <Typography className={boldeText}>
            {protectedData.protectString(fixedAccount.accountNumber)}
          </Typography>
        </div>
      ) : null}
      {fixedAccount && fixedAccount.swiftCode ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('ccSwiftCode')}: </Typography>
          <Typography className={boldeText}>
            {protectedData.protectString(fixedAccount.swiftCode)}
          </Typography>
        </div>
      ) : null}
      {fixedAccount && fixedAccount.clabe ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('ccClabeNumber')}: </Typography>
          <Typography className={boldeText}>
            {protectedData.protectString(fixedAccount.clabe)}
          </Typography>
        </div>
      ) : null}
      {hasUnsupportedClabe && (
        <Alert severity="warning" className="mb-4">
          {unsupportedBankInfo
            ? t('clabeUnsupportedBankSpecific', {
                bankName: t(unsupportedBankInfo.name),
              })
            : t('clabeUnsupportedBank')}
        </Alert>
      )}

      <div className={wrapperClasses}>
        <Typography className="!text-lg">{t('recipientGets')}: </Typography>
        <Typography className={boldeText}>
          {!isForward &&
            `${formatCurrency(
              !reverse ? amount : resultAmount,
              DecimalDigits.DEFAULT_DIGITS,
              DecimalDigits.DEFAULT_MIN_DIGITS,
              currency
            )} ${currency}`}
          {isForward &&
            forwardValues?.amount &&
            forwardValues.currency &&
            `${formatCurrency(
              forwardValues?.amount,
              DecimalDigits.DEFAULT_DIGITS,
              DecimalDigits.DEFAULT_MIN_DIGITS,
              forwardValues?.currency
            )} ${forwardValues?.currency}`}
        </Typography>
      </div>
      {!isForward ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('amountToSend3')} </Typography>
          <Typography className={boldeText}>
            {`${formatCurrency(
              !reverse ? resultAmount : amount,
              DECIMALS_TO_SHOW,
              DECIMALS_TO_SHOW,
              payable?.desCurrency ?? clientCurrency
            )} ${payable?.desCurrency ?? clientCurrency}`}
          </Typography>
        </div>
      ) : null}
      {isForward && forwardValues?.rate ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('forwardRate')}</Typography>
          <Typography className={boldeText}>
            {Number(forwardValues.rate).toFixed(DECIMALS_TO_SHOW)}
          </Typography>
        </div>
      ) : null}
      {isForward && forwardValues?.des_amount ? (
        <div className={wrapperClasses}>
          <Typography className="!text-lg">{t('desAmount')}:</Typography>
          <Typography className={boldeText}>
            {formatCurrency(
              forwardValues.des_amount,
              DecimalDigits.DEFAULT_DIGITS,
              DecimalDigits.DEFAULT_MIN_DIGITS,
              forwardValues.des_currency
            )}{' '}
            {forwardValues.des_currency}
          </Typography>
        </div>
      ) : null}
      {data.feeDescription && data.feeDescription.list_fees
        ? data.feeDescription.list_fees.map((fee) =>
            fee.amount > AMOUNT_MONEY.ZERO_NUMBER ? (
              <div key={fee.strategy_code} className={wrapperClasses}>
                <Typography className="!text-lg">{t(fee.name)}:</Typography>
                <Typography className={boldeText}>
                  {' '}
                  {formatCurrency(
                    fee.amount,
                    DecimalDigits.DEFAULT_DIGITS,
                    DecimalDigits.DEFAULT_MIN_DIGITS,
                    data.clientCurrency
                  )}{' '}
                  {data.clientCurrency}
                </Typography>
              </div>
            ) : (
              ''
            )
          )
        : null}

      <div className={wrapperClasses}>
        <Typography className="!text-lg">
          {t(!dueDate ? 'shouldArrive' : 'scheduledDate')}:
        </Typography>
        <Typography className={boldeText}>
          {dueDate
            ? dueDate.toLocaleString(DateTime.DATE_SHORT)
            : DateTime.now().toLocaleString(DateTime.DATE_SHORT)}
        </Typography>
      </div>
    </div>
  );
};

export default DetailView;
